import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import Icon from './Icon';
import { useAuth } from '../context/AuthContext';

const NAV = [
  { label: 'Home', path: '/' },
  { label: 'Longevity', path: '/longevity' },
  { label: 'Wellbeing', path: '/wellbeing' },
  { label: 'Resources', path: '/resources' },
  { label: 'My Login', path: '/my-login' },
];

export default function Header() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const isAuthed = user && typeof user === 'object';

  const isActive = (path) => (path === '/' ? pathname === '/' : pathname.startsWith(path));

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-surface/90 backdrop-blur-xl border-b border-surface-container">
      <div className="h-20 w-full px-margin-mobile lg:px-margin flex items-center justify-between gap-gutter">
        {/* Brand */}
        <div className="flex items-center gap-space-lg">
          <Link
            to="/"
            onClick={() => setOpen(false)}
            data-testid="header-logo-link"
            aria-label="OVERALL WELLBEING — Live Better. Live Longer. — Home"
            className="flex items-center group focus:outline-none"
          >
            <img
              src="/logo-lockup.png"
              alt="OVERALL WELLBEING — Live Better. Live Longer."
              className="h-11 sm:h-14 w-auto group-hover:opacity-90 transition-opacity"
            />
          </Link>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden xl:flex items-center gap-space-xl">
          {NAV.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              aria-current={isActive(item.path) ? 'page' : undefined}
              className={
                isActive(item.path)
                  ? 'transition-colors text-primary font-title-md text-title-md relative after:absolute after:bottom-[-26px] after:left-0 after:right-0 after:h-[2px] after:bg-surface-tint'
                  : 'font-title-md text-title-md text-on-surface-variant hover:text-on-surface transition-colors'
              }
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-space-md">
          <Link
            to="/wellbeing"
            aria-label="Search Platform"
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-surface-container-low text-on-surface hover:bg-surface-container hover:text-primary transition-colors focus:outline-none"
          >
            <Icon name="search" className="text-[20px]" />
          </Link>
          <Link
            to="/start"
            data-testid="header-start-cta"
            className="hidden md:inline-flex items-center px-space-lg h-10 rounded-lg bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-wider shadow-[0_4px_16px_rgba(240,40,223,0.25)] hover:bg-primary transition-all"
          >
            Start Your Wellbeing
          </Link>
          <Link to="/my-login" data-testid="header-account-link" className="w-8 h-8 rounded-full bg-primary flex items-center justify-center hover:bg-surface-tint transition-colors">
            <Icon name="person" className="text-on-primary text-[18px]" />
          </Link>
          {isAuthed && (
            <button
              type="button"
              data-testid="header-logout-button"
              onClick={logout}
              aria-label="Sign out"
              title="Sign out"
              className="hidden md:inline-flex w-10 h-10 items-center justify-center rounded-lg bg-surface-container-low text-on-surface hover:bg-surface-container hover:text-primary transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          )}
          {/* Mobile menu toggle */}
          <button
            type="button"
            aria-label="Toggle navigation"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-surface-container-low text-on-surface hover:bg-surface-container transition-colors"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav Panel */}
      {open && (
        <div className="xl:hidden border-t border-surface-container bg-surface/98 backdrop-blur-xl">
          <nav className="flex flex-col px-margin-mobile py-space-md">
            {NAV.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setOpen(false)}
                className={
                  'py-space-md border-b border-surface-container last:border-0 font-title-md text-title-md uppercase tracking-wide transition-colors ' +
                  (isActive(item.path) ? 'text-primary' : 'text-on-surface-variant hover:text-on-surface')
                }
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/start"
              data-testid="header-start-cta-mobile"
              onClick={() => setOpen(false)}
              className="mt-space-md inline-flex items-center justify-center px-space-lg h-11 rounded-lg bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-wider shadow-[0_4px_16px_rgba(240,40,223,0.25)]"
            >
              Start Your Wellbeing
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
